import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SEO from "@/components/seo/SEO";
import { GoldButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard } from "@/components/common/ui";
import { Cpu, Sparkle, ShieldCheck, Buildings, ChartLineUp, Robot, LinkSimple, Lightning, Stack } from "@phosphor-icons/react";

const ICONS = [Stack, Cpu, Sparkle, Buildings, Lightning, LinkSimple, ShieldCheck, ChartLineUp, Robot];

export default function Platform() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const pillars = t("platform.pillars", { returnObjects: true }) || [];

  return (
    <>
      <SEO title={lang === "nl" ? "Platform — BHT One" : "Platform — BHT One"} path="/platform" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{t("platform.eyebrow")}</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl md:text-7xl"
          >
            {t("platform.h1")}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">{t("platform.sub")}</p>
        </div>
      </section>

      <section className="relative border-y border-white/[0.06] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {pillars.map((p, i) => {
              const Icon = ICONS[i] || Stack;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex flex-col rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-colors hover:border-bht-gold/30"
                  data-testid={`platform-pillar-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon size={26} weight="thin" className="text-bht-gold" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">P/{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="mt-8 font-serif text-2xl group-hover:text-bht-gold">{p.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Architecture story */}
      <section className="relative py-32 lg:py-44">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={lang === "nl" ? "Architectuur" : "Architecture"}
              title={lang === "nl" ? "Van event, naar data, naar beslissing." : "From event, to data, to decision."}
              sub={lang === "nl"
                ? "Elke actie in BHT One is een event. Events stromen door de bus, worden geïndexeerd, verrijkt door AI en aangeboden aan de juiste module — in real-time."
                : "Every action inside BHT One is an event. Events flow through the bus, get indexed, enriched by AI, and surface in the right module — in real-time."}
            />
          </div>
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-black/40 p-8">
              <div className="grid gap-3">
                {[
                  { l: "01 · Event Bus", d: "Kafka-style · at-least-once · replayable" },
                  { l: "02 · Domain Layer", d: "Reservations · Guests · Rooms · Revenue · Inventory" },
                  { l: "03 · AI Substrate", d: "Assistant · Knowledge · Automation · Insights · Voice" },
                  { l: "04 · Modules", d: "18 modules — same identity, design & APIs" },
                  { l: "05 · Open APIs", d: "REST · Webhooks · SDKs · Developer Portal" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-white/[0.05] bg-white/[0.02] px-4 py-3">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-bht-gold">{r.l}</span>
                    <span className="text-sm text-white/70">{r.d}</span>
                  </div>
                ))}
              </div>
              <div className="pointer-events-none absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-bht-gold/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] py-32">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <SectionHeading align="center" eyebrow="Talk to engineering" title={lang === "nl" ? "Een technische diepgang met onze architecten." : "A technical deep-dive with our architects."} />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton to={`/${lang}/book-demo`}>{t("cta.primary")}</GoldButton>
          </div>
        </div>
      </section>
    </>
  );
}
